/**
 * Shared formatting for the Common Crawl rank tool.
 *
 * The result card and the server-rendered per-domain summary have to quote the
 * same figure for the same domain, so the percentile math lives here rather
 * than in either renderer.
 */

/**
 * Turn an ordinal rank into a "top X%" figure relative to the tracked set.
 *
 * We deliberately use the tracked domain count (the top ~10M by Harmonic
 * Centrality) as the denominator rather than the full Common Crawl graph. The
 * full graph has hundreds of millions of nodes, many of them dead or single-page
 * domains, so a percentile against it overstates a site's standing and clashes
 * with the tier labels (which are also defined within the tracked set). Ranking
 * against the tracked set keeps the percentile and the tier telling one story.
 *
 * Global ranks (e.g. the PageRank rank, which is ordinal across the whole graph)
 * can exceed the tracked count, so we clamp the result to 100%.
 *
 * Returns null when inputs are missing.
 */
export function formatTopPercent(
	rank: number | null,
	trackedCount: number,
): string | null {
	if (rank == null || rank <= 0 || trackedCount <= 0) {
		return null;
	}
	const topPercent = Math.min(100, (rank / trackedCount) * 100);
	if (topPercent < 0.01) {
		return "0.01";
	}
	if (topPercent < 1) {
		return topPercent.toFixed(2);
	}
	return topPercent.toFixed(1);
}

/** Format an ordinal rank with thousands separators, e.g. 12345 -> "#12,345". */
export function formatRank(rank: number): string {
	return `#${rank.toLocaleString("en-US")}`;
}
